import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ArrowUp, ArrowDown, Search, X, Music2 } from "lucide-react";
import api, { formatApiError } from "@/lib/upr-api";
import TagInput from "@/components/TagInput";
import FileUpload from "@/components/FileUpload";

const EMPTY = { id: "", title: "", slug: "", description: "", tags: [], tracks: [], cover_image_url: "", display_order: 100 };

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function PlaylistsAdmin() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);   // null = list view, object = editor
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = () =>
    api.get("/playlists").then(({ data }) => setItems(Array.isArray(data) ? data : []));

  useEffect(() => {
    load();
    api.get("/playlists/_catalog")
      .then(({ data }) => setCatalog(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const startNew = () => setEditing({ ...EMPTY, tags: [], tracks: [] });
  const startEdit = (p) => setEditing({
    id: p.id,
    title: p.title,
    slug: p.slug,
    description: p.description || "",
    tags: p.tags || [],
    tracks: (p.tracks || []).map((t) => ({ release_id: t.release_id, track_index: t.track_index })),
    cover_image_url: p.cover_image_url || "",
    display_order: p.display_order ?? 100,
  });
  const close = () => setEditing(null);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete playlist "${p.title}"?`)) return;
    try {
      await api.delete(`/playlists/${p.id}`);
      toast.success("Playlist deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        slug: slugify(editing.slug || editing.title),
        description: (editing.description || "").trim(),
        tags: editing.tags,
        tracks: editing.tracks,
        cover_image_url: (editing.cover_image_url || "").trim(),
        display_order: Number(editing.display_order) || 100,
      };
      if (editing.id) await api.put(`/playlists/${editing.id}`, payload);
      else            await api.post("/playlists", payload);
      toast.success("Saved.");
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <section data-testid="playlists-admin">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-heading font-extrabold text-2xl tracking-tight">Playlists</h2>
          <button className="upr-btn" onClick={startNew} data-testid="playlist-add">
            <Plus size={14} /> New playlist
          </button>
        </div>
        {items.length === 0 && (
          <div className="text-sm" style={{ color: "var(--upr-muted)" }}>No playlists yet.</div>
        )}
        <ul>
          {items.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-4 py-3 border-b"
              style={{ borderColor: "var(--upr-border)" }}
              data-testid={`playlist-row-${p.id}`}
            >
              <div className="w-14 h-14 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)", background: "var(--upr-surface)" }}>
                {p.cover_url
                  ? <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--upr-muted)" }}><Music2 size={18} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold truncate">{p.title}</div>
                <div className="text-sm truncate" style={{ color: "var(--upr-muted)" }}>
                  /playlists/{p.slug} · {p.track_count || 0} tracks
                </div>
              </div>
              <div className="flex gap-2">
                <button className="upr-btn upr-btn-ghost" onClick={() => startEdit(p)} data-testid={`playlist-row-${p.id}-edit`}>
                  <Edit3 size={12} /> Edit
                </button>
                <button className="upr-btn upr-btn-ghost" onClick={() => onDelete(p)} data-testid={`playlist-row-${p.id}-delete`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <PlaylistEditor
      editing={editing}
      setEditing={setEditing}
      catalog={catalog}
      search={search}
      setSearch={setSearch}
      saving={saving}
      save={save}
      close={close}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                             */
/* ------------------------------------------------------------------ */
function PlaylistEditor({ editing, setEditing, catalog, search, setSearch, saving, save, close }) {
  const trackKey = (t) => `${t.release_id}::${t.track_index}`;
  const chosen = useMemo(() => new Set((editing.tracks || []).map(trackKey)), [editing.tracks]);

  const filteredCatalog = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return catalog;
    return catalog.filter((c) =>
      (c.track_title || "").toLowerCase().includes(s) ||
      (c.release_title || "").toLowerCase().includes(s) ||
      (c.artist_name || "").toLowerCase().includes(s)
    );
  }, [catalog, search]);

  const addTrack = (c) => {
    const ref = { release_id: c.release_id, track_index: c.track_index };
    if (chosen.has(trackKey(ref))) return;
    setEditing({ ...editing, tracks: [...editing.tracks, ref] });
  };
  const removeAt = (i) =>
    setEditing({ ...editing, tracks: editing.tracks.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= editing.tracks.length) return;
    const next = [...editing.tracks];
    [next[i], next[j]] = [next[j], next[i]];
    setEditing({ ...editing, tracks: next });
  };

  // Denormalise the chosen refs against the catalogue so the "Selected tracks"
  // list can render titles / covers without an extra fetch.
  const chosenHydrated = useMemo(() => {
    const idx = new Map();
    for (const c of catalog) idx.set(`${c.release_id}::${c.track_index}`, c);
    return editing.tracks.map((r) => ({ ...r, meta: idx.get(trackKey(r)) }));
  }, [editing.tracks, catalog]);

  return (
    <section data-testid="playlist-editor">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="upr-eyebrow mb-1">{editing.id ? "Edit playlist" : "New playlist"}</div>
          <h2 className="font-heading font-extrabold text-2xl tracking-tight">
            {editing.title || "Untitled"}
          </h2>
        </div>
        <div className="flex gap-2">
          <button className="upr-btn upr-btn-ghost" onClick={close} data-testid="playlist-cancel">Cancel</button>
          <button className="upr-btn" onClick={save} disabled={saving} data-testid="playlist-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <label className="block">
          <span className="upr-eyebrow block mb-2">Title</span>
          <input
            type="text"
            value={editing.title}
            onChange={(e) => setEditing({ ...editing, title: e.target.value })}
            data-testid="playlist-title-input"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <label className="block">
          <span className="upr-eyebrow block mb-2">Slug (URL)</span>
          <input
            type="text"
            value={editing.slug}
            placeholder={slugify(editing.title) || "auto-from-title"}
            onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
            data-testid="playlist-slug-input"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="upr-eyebrow block mb-2">Description (short theme)</span>
          <input
            type="text"
            value={editing.description}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            placeholder="e.g. Music for swimming pools"
            data-testid="playlist-description-input"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
        <div className="block md:col-span-2">
          <FileUpload
            label="Cover image (optional — falls back to first track's release artwork)"
            value={editing.cover_image_url}
            onChange={(v) => setEditing({ ...editing, cover_image_url: v })}
            kind="image"
            testid="playlist-cover"
          />
        </div>
        <div className="block md:col-span-2">
          <TagInput
            label="Tags"
            value={editing.tags || []}
            onChange={(tags) => setEditing({ ...editing, tags })}
            testid="playlist-tags"
          />
        </div>
        <label className="block">
          <span className="upr-eyebrow block mb-2">Display order (lower = shown first)</span>
          <input
            type="number"
            value={editing.display_order ?? 100}
            onChange={(e) =>
              setEditing({ ...editing, display_order: e.target.value === "" ? 100 : Number(e.target.value) })
            }
            data-testid="playlist-order-input"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)]"
            style={{ borderColor: "var(--upr-border)" }}
          />
        </label>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-4">
        {/* -------- Chosen tracks -------- */}
        <div>
          <div className="upr-eyebrow mb-3">Tracks in playlist ({editing.tracks.length})</div>
          {editing.tracks.length === 0 ? (
            <div
              className="text-sm p-4"
              style={{ color: "var(--upr-muted)", border: "1px dashed var(--upr-border)" }}
              data-testid="playlist-selected-empty"
            >
              Pick tracks from your catalog on the right.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="playlist-selected-list">
              {chosenHydrated.map((t, i) => (
                <li
                  key={`${t.release_id}-${t.track_index}-${i}`}
                  className="flex items-center gap-3 p-2 border"
                  style={{ borderColor: "var(--upr-border)" }}
                  data-testid={`playlist-selected-${i}`}
                >
                  <div className="w-10 h-10 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                    {t.meta?.cover_url && <img src={t.meta.cover_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm truncate">
                      {t.meta ? t.meta.track_title : <span style={{ color: "var(--upr-muted)" }}>Missing track (release deleted?)</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--upr-muted)" }}>
                      {t.meta ? `${t.meta.artist_name} — ${t.meta.release_title}` : `${t.release_id}#${t.track_index}`}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      className="upr-btn upr-btn-ghost"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      title="Move up"
                      data-testid={`playlist-selected-${i}-up`}
                    ><ArrowUp size={12} /></button>
                    <button
                      type="button"
                      className="upr-btn upr-btn-ghost"
                      onClick={() => move(i, 1)}
                      disabled={i === editing.tracks.length - 1}
                      title="Move down"
                      data-testid={`playlist-selected-${i}-down`}
                    ><ArrowDown size={12} /></button>
                    <button
                      type="button"
                      className="upr-btn upr-btn-ghost"
                      onClick={() => removeAt(i)}
                      title="Remove"
                      data-testid={`playlist-selected-${i}-remove`}
                    ><X size={12} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* -------- Catalog picker -------- */}
        <div>
          <div className="upr-eyebrow mb-3">Add from catalog ({catalog.length} tracks)</div>
          <label className="block relative mb-3">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--upr-muted)" }} />
            <input
              type="text"
              placeholder="Search track, release or artist…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="playlist-search"
              className="w-full pl-8 p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
              style={{ borderColor: "var(--upr-border)" }}
            />
          </label>
          <div
            className="max-h-[420px] overflow-y-auto border"
            style={{ borderColor: "var(--upr-border)" }}
            data-testid="playlist-catalog-list"
          >
            {filteredCatalog.length === 0 && (
              <div className="p-4 text-xs" style={{ color: "var(--upr-muted)" }}>No matches.</div>
            )}
            {filteredCatalog.map((c) => {
              const chosenKey = `${c.release_id}::${c.track_index}`;
              const already = chosen.has(chosenKey);
              return (
                <div
                  key={chosenKey}
                  className="flex items-center gap-3 p-2 border-b last:border-b-0"
                  style={{ borderColor: "var(--upr-border)" }}
                  data-testid={`playlist-catalog-row-${c.release_id}-${c.track_index}`}
                >
                  <div className="w-10 h-10 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                    {c.cover_url && <img src={c.cover_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-sm truncate">
                      {c.track_title}
                      {!c.has_audio && <span className="ml-2 upr-eyebrow" style={{ fontSize: "0.55rem", color: "var(--upr-muted)" }}>no audio</span>}
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--upr-muted)" }}>
                      {c.artist_name} — {c.release_title}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="upr-btn upr-btn-ghost shrink-0"
                    onClick={() => addTrack(c)}
                    disabled={already}
                    data-testid={`playlist-catalog-add-${c.release_id}-${c.track_index}`}
                  >
                    {already ? "Added" : <><Plus size={12} /> Add</>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
